import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Timer, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  const s = Math.floor(diff / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

const pad = (n) => String(n).padStart(2, "0");

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-paper text-ink font-heading font-black text-2xl sm:text-3xl tabular-nums px-3 py-1.5 border border-ink min-w-[3rem] text-center">{pad(value)}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-paper/70 mt-1">{label}</div>
    </div>
  );
}

export default function Deals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/deals").then((r) => setData(r.data)).catch(() => setData({ deals: [], soonest_drop_at: null }));
  }, []);

  const cd = useCountdown(data?.soonest_drop_at);
  const deals = data?.deals || [];
  const pct = data?.markdown_pct ?? 5;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8" data-testid="deals-page">
      {/* HERO */}
      <div className="bg-ink text-paper border-2 border-ink p-6 sm:p-10 mb-8 relative overflow-hidden">
        <div className="inline-flex items-center gap-2 bg-danger text-white font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1 mb-4">
          <Flame className="h-3.5 w-3.5" /> Markdown Zone
        </div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          PRICES THAT<br />KEEP FALLING
        </h1>
        <p className="font-mono text-sm text-paper/80 mt-4 max-w-xl leading-relaxed">
          Every two weeks, unsold surplus drops another <strong className="text-discount">{pct}%</strong> — automatically —
          until it's gone. When it sells, it's gone for good. Grab it before it's gone.
        </p>

        {deals.length > 0 && cd && (
          <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="deals-countdown">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-discount">
              <Timer className="h-4 w-4" /> Next price drop in
            </div>
            <div className="flex items-end gap-2">
              <Unit value={cd.d} label="Days" />
              <span className="font-heading font-black text-2xl pb-4">:</span>
              <Unit value={cd.h} label="Hrs" />
              <span className="font-heading font-black text-2xl pb-4">:</span>
              <Unit value={cd.m} label="Min" />
              <span className="font-heading font-black text-2xl pb-4">:</span>
              <Unit value={cd.s} label="Sec" />
            </div>
          </div>
        )}
      </div>

      {/* GRID */}
      {data === null ? (
        <div className="font-mono text-subtle">Loading deals…</div>
      ) : deals.length === 0 ? (
        <div className="border-2 border-dashed border-subtle p-12 text-center" data-testid="deals-empty">
          <TrendingDown className="h-8 w-8 mx-auto mb-3 text-subtle" />
          <p className="font-mono text-sm text-subtle max-w-md mx-auto">
            No active markdowns right now. Items land here automatically once they've gone unsold for two weeks — check back soon.
          </p>
          <Link to="/" className="inline-block mt-5 bg-ink text-paper px-6 h-10 leading-10 font-mono text-xs uppercase tracking-wider hover:bg-link transition-colors">
            Browse Full Catalog
          </Link>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between border-b-2 border-ink pb-3 mb-6">
            <div className="font-heading font-black text-2xl tracking-tighter">{deals.length} MARKED-DOWN ITEM{deals.length === 1 ? "" : "S"}</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-subtle hidden sm:block">Freshest markdowns first</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {deals.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
